/**
 * Form Renderer
 *
 * Renders a form into a Shadow DOM given pre-fetched PublicFormData.
 * Performs identity stitching on submit via the SegmentflowWeb analytics instance.
 *
 * Vanilla DOM manipulation — no framework dependencies.
 */

import DOMPurify from "dompurify";
import { createElement, createShadowContainer } from "./utils.js";
import { generateCSS, type StyleConfig } from "./form-styles.js";
import { submitForm, type SubmitData } from "./form-submit.js";
import { injectHoneypot, setFormLoadTimestamp, getFormLoadTimestamp } from "./anti-spam.js";
import type { SegmentflowWeb } from "../../web.js";
import { writeSfId } from "../../web.js";

// ============================================================================
// Types
// NOTE: Defined locally to avoid importing from @segmentflow/api-schemas in
// the CDN bundle. Shapes must stay aligned with the shared Zod schemas.
// ============================================================================

interface FormImage {
  url: string;
  alt?: string;
  width?: number;
  height?: number;
}

interface FormConfig {
  version: string;
  image?: FormImage;
  heading?: string;
  description?: string;
  fields: FieldConfig[];
  submitButtonText: string;
  submitButtonLoadingText?: string;
  layout: string;
}

interface FieldConfig {
  id: string;
  type: string;
  name: string;
  label: string;
  placeholder?: string;
  required: boolean;
  order: number;
}

export interface PublicFormData {
  id: string;
  /** WebsiteForm pairing ID — used for submission endpoint */
  websiteFormId: string;
  displayType: "Popup" | "Inline";
  config: FormConfig;
  style: StyleConfig;
  displayRules?: Record<string, unknown>;
  customCss?: string;
  consentText?: string;
  doubleOptIn: boolean;
  successMessage?: string;
  leadMagnetText?: string;
}

// ============================================================================
// Internal helpers
// ============================================================================

function renderField(field: FieldConfig): HTMLElement {
  const wrapper = createElement("div", { class: "sf-field" });

  if (field.type !== "hidden") {
    const label = createElement("label", {
      class: "sf-label",
      for: `sf-input-${field.id}`,
    }, [field.label]);
    wrapper.appendChild(label);
  }

  const input = createElement("input", {
    class: "sf-input",
    id: `sf-input-${field.id}`,
    type: field.type === "email" ? "email" : "text",
    name: field.name,
    ...(field.placeholder ? { placeholder: field.placeholder } : {}),
    ...(field.required ? { required: "" } : {}),
    ...(field.type === "hidden" ? { type: "hidden" } : {}),
    // Prevent password managers from attaching autofill overlays
    autocomplete: "off",
    "data-1p-ignore": "",       // 1Password
    "data-lpignore": "true",    // LastPass
    "data-bwignore": "",        // Bitwarden
    "data-protonpass-ignore": "", // Proton Pass
  });

  wrapper.appendChild(input);

  // Error message placeholder
  const errorEl = createElement("div", {
    class: "sf-error-text",
    id: `sf-error-${field.id}`,
    "aria-live": "polite",
  });
  errorEl.style.display = "none";
  wrapper.appendChild(errorEl);

  return wrapper;
}

function renderConsentCheckbox(consentText: string): HTMLElement {
  const wrapper = createElement("div", { class: "sf-field sf-consent" });

  const label = createElement("label", {
    style: "display:flex;align-items:flex-start;gap:8px;font-size:0.875em;cursor:pointer;",
  });

  const checkbox = createElement("input", {
    type: "checkbox",
    name: "consent",
    required: "",
    style: "margin-top:2px;",
  });

  const text = createElement("span");
  text.innerHTML = DOMPurify.sanitize(consentText, {
    ALLOWED_TAGS: ["a", "strong", "em", "b", "i", "br", "span"],
    ALLOWED_ATTR: ["href", "target", "rel", "class"],
  });

  label.appendChild(checkbox);
  label.appendChild(text);
  wrapper.appendChild(label);

  return wrapper;
}

function showSuccess(
  container: HTMLElement,
  message: string,
  redirectUrl?: string,
): void {
  while (container.firstChild) container.removeChild(container.firstChild);
  const successDiv = createElement("div", { class: "sf-success" });
  const msgEl = createElement("div", { class: "sf-success-message" }, [
    message,
  ]);
  successDiv.appendChild(msgEl);
  container.appendChild(successDiv);

  if (redirectUrl) {
    setTimeout(() => {
      window.location.href = redirectUrl;
    }, 1500);
  }
}

function showError(container: HTMLElement, message: string): void {
  // Remove existing error banners
  const existing = container.querySelector(".sf-error-banner");
  if (existing) existing.remove();

  const banner = createElement("div", {
    class: "sf-error-banner",
    role: "alert",
    "aria-live": "assertive",
  }, [message]);
  container.prepend(banner);

  // Auto-remove after 5 seconds
  setTimeout(() => banner.remove(), 5000);
}

// ============================================================================
// Main Render Function
// ============================================================================

/**
 * Render a form into `host` using a Shadow DOM.
 *
 * @param host       - The container element (popup box or inline div)
 * @param formData   - Pre-fetched form config from the listing endpoint
 * @param apiUrl     - API base URL for form submission
 * @param analytics  - SegmentflowWeb instance for identity stitching (optional)
 */
export async function renderForm(
  host: HTMLElement,
  formData: PublicFormData,
  apiUrl: string,
  analytics?: SegmentflowWeb,
): Promise<void> {
  const shadow = createShadowContainer(host);
  const config = formData.config;
  const style = formData.style;

  // Inject CSS
  const styleEl = document.createElement("style");
  styleEl.textContent = generateCSS(style, formData.customCss);
  shadow.appendChild(styleEl);

  // Container
  const container = createElement("div", { class: "sf-form-container" });
  shadow.appendChild(container);

  // Image (logo or hero)
  if (config.image?.url) {
    const imgWrapper = createElement("div", { class: "sf-image" });
    const img = createElement("img", {
      src: config.image.url,
      alt: config.image.alt ?? "",
      ...(config.image.width ? { width: String(config.image.width) } : {}),
      ...(config.image.height ? { height: String(config.image.height) } : {}),
    });
    imgWrapper.appendChild(img);
    container.appendChild(imgWrapper);
  }

  // Heading & description
  if (config.heading || config.description) {
    const header = createElement("div", { class: "sf-header" });
    if (config.heading) {
      header.appendChild(createElement("h2", { class: "sf-heading" }, [config.heading]));
    }
    if (config.description) {
      header.appendChild(createElement("p", { class: "sf-description" }, [config.description]));
    }
    container.appendChild(header);
  }

  // Lead magnet text — tells visitors what they'll receive
  if (formData.leadMagnetText) {
    const lmText = createElement("p", { class: "sf-lead-magnet-text" }, [formData.leadMagnetText]);
    container.appendChild(lmText);
  }

  // Build form
  const layoutClass =
    config.layout === "horizontal"
      ? "sf-layout-horizontal"
      : config.layout === "inline"
        ? "sf-layout-inline"
        : "sf-layout-vertical";

  const form = createElement("form", {
    class: `sf-form ${layoutClass}`,
    novalidate: "",
    "aria-label": "Signup form",
  }) as HTMLFormElement;

  // Render fields sorted by order
  const sortedFields = [...config.fields].sort((a, b) => a.order - b.order);
  for (const field of sortedFields) {
    form.appendChild(renderField(field));
  }

  // Consent checkbox
  if (formData.consentText) {
    form.appendChild(renderConsentCheckbox(formData.consentText));
  }

  // Submit button
  const submitBtn = createElement(
    "button",
    { type: "submit", class: "sf-submit-btn" },
    [config.submitButtonText || "Subscribe"],
  );
  form.appendChild(submitBtn);

  // Anti-spam
  injectHoneypot(form);
  setFormLoadTimestamp(form);

  // Handle submission
  form.addEventListener("submit", async (e) => {
    e.preventDefault();

    const btn = submitBtn;
    const originalText = btn.textContent ?? "";
    btn.disabled = true;
    btn.textContent = config.submitButtonLoadingText ?? "Submitting...";

    // Collect field values
    const fields: Record<string, unknown> = {};
    let email = "";
    for (const field of sortedFields) {
      const input = shadow.getElementById(
        `sf-input-${field.id}`,
      ) as HTMLInputElement | null;
      if (input) {
        if (field.type === "email") {
          email = input.value.trim();
        } else {
          fields[field.name] = input.value;
        }
      }
    }

    // Client-side email validation
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      const emailError = shadow.getElementById("sf-error-email");
      const emailInput = shadow.getElementById("sf-input-email");
      if (emailError) {
        emailError.textContent = "Please enter a valid email address.";
        emailError.style.display = "block";
      }
      if (emailInput) {
        emailInput.classList.add("sf-input-error");
      }
      btn.disabled = false;
      btn.textContent = originalText;
      return;
    }

    // Get honeypot value
    const honeypotInput = form.querySelector(
      'input[name="_hp"]',
    ) as HTMLInputElement | null;

    // Consent
    const consentCheckbox = form.querySelector(
      'input[name="consent"]',
    ) as HTMLInputElement | null;

    // Get anonymous ID from the analytics instance
    const anonymousId = analytics?.getAnonymousId?.();

    const submitData: SubmitData = {
      email,
      fields,
      consentGiven: consentCheckbox?.checked ?? false,
      pageUrl: window.location.href,
      referrerUrl: document.referrer || undefined,
      _hp: honeypotInput?.value ?? "",
      _ts: getFormLoadTimestamp(form),
      anonymousId,
    };

    try {
      const result = await submitForm(apiUrl, formData.websiteFormId, submitData);

      if (result.success) {
        // ── Client-side cookie continuity ───────────────────────────────────
        // The server-side form-submit endpoint now creates the identify
        // user_event itself (see services/node/api/src/services/v1/public/
        // form-submission.service.ts), so we no longer call analytics.identify()
        // here — that produced a redundant second HTTP call to /ingest/batch
        // and could land asynchronously after the user navigated away.
        //
        // We still update the sf_id cookie so subsequent page-views in the
        // same session carry the email and stitch to the same profile.
        if (analytics) {
          writeSfId({ e: email });
        }

        showSuccess(
          container,
          result.message || formData.successMessage || "Thank you! You've been subscribed.",
          result.redirectUrl,
        );
      } else {
        showError(container, result.message);
        btn.disabled = false;
        btn.textContent = originalText;
      }
    } catch {
      // Network error — re-enable the button so the user can retry
      showError(container, "Unable to connect. Please check your internet connection and try again.");
      btn.disabled = false;
      btn.textContent = originalText;
    }
  });

  container.appendChild(form);
}
